/*
  Reads the recruitment data out of the "Scheduling OIDS-TC Master" workbook
  (already loaded with XLSX.read) into the payload POST /recruitment/import expects.

  Uses the values Excel last calculated, so the workbook must have been saved
  after its formulas recalculated. Dates are read as raw serials (not cellDates)
  to avoid SheetJS's local-timezone shift.
*/
import * as XLSX from 'xlsx';

const ROSTER_SHEET = 'Recruitment Roster';
const DASHBOARD_SHEET = 'Recruitment Dashboard';

// Pass as XLSX.read's `sheets` option so the rest of the (large) workbook is skipped.
export const RECRUITMENT_SHEETS = [ROSTER_SHEET, DASHBOARD_SHEET];

// DB column -> roster header. Headers are matched exactly, typos included ("Partner Decsision").
const CANDIDATE_COLUMNS = {
  CandidateName: 'Name',
  Status: 'Status',
  ActiveProcess: 'Active Process?',
  Experience: 'Experience',
  Level: 'Level',
  Office: 'Office',
  Gender: 'Gender',
  ServiceLine: 'Service Line',
  FY: 'FY',
  PeopleSharingDate: 'People Sharing Date',
  ManagerInterviewRequestDate: 'Manager Interview Request Date',
  Manager: 'Manager for Interview',
  ManagerInterviewDate: 'Manager Interview Date',
  ManagerDecision: 'Manager Decision',
  PartnerInterviewRequestDate: 'Partner Interview Request Date',
  Partner: 'Partner for Interview',
  PartnerInterviewDate: 'Partner Interview Date',
  PartnerDecision: 'Partner Decsision',
  ProposalRequestDate: 'Proposal Request Date',
  ProposalSubmissionDate: 'Proposal Submission Date',
  CandidateDecisionDate: 'Candidate Decision Date',
  JobStartDate: 'Job Start Date',
  RejectionReason: 'Rejection/Withdrawal Reason',
  FollowUpDate: 'Follow-up date (ad-hoc)',
  FollowUpRequired: 'Follow-up required?',
  LastUpdate: 'Last update',
  DaysOfProcess: '# Days of process'
};
const DATE_COLUMNS = new Set([
  'PeopleSharingDate',
  'ManagerInterviewRequestDate',
  'ManagerInterviewDate',
  'PartnerInterviewRequestDate',
  'PartnerInterviewDate',
  'ProposalRequestDate',
  'ProposalSubmissionDate',
  'CandidateDecisionDate',
  'JobStartDate',
  'FollowUpDate',
  'LastUpdate'
]);

// Dashboard layout: metric labels in column P, month-start dates in row 6, month names in row 8.
const LABEL_COL = 15;
const DATE_ROW = 5;
const MONTH_NAME_ROW = 7;
const FIRST_METRIC_ROW = 8;
const METRICS_END_LABEL = '*incl. Transfered';

const pad = n => String(n).padStart(2, '0');

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);

// Excel serial -> 'YYYY-MM-DD' (in UTC, so no timezone shift; exact for any date after Feb 1900).
// Serial 0 (a blank shown as 00:00) is treated as empty.
function serialToDate(value) {
  if (typeof value !== 'number' || value < 1) return null;
  const d = new Date(EXCEL_EPOCH_MS + Math.floor(value) * 86400000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function cleanText(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === '' || text.startsWith('#') ? null : text;
}

// Fiscal years start in June: June 2026 – May 2027 is FY27.
export function fiscalYearOf(year, month) {
  return `FY${String(month >= 6 ? year + 1 : year).slice(-2)}`;
}

function sheetRows(workbook, name) {
  const sheet = workbook.Sheets[name];
  if (!sheet) throw new Error(`Sheet "${name}" not found in the workbook.`);
  // Read from A1 even when the used range starts later, so row/column indexes match the sheet.
  const range = XLSX.utils.decode_range(sheet['!ref']);
  range.s = { r: 0, c: 0 };
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null, blankrows: true, range });
}

function parseCandidates(workbook) {
  const rows = sheetRows(workbook, ROSTER_SHEET);
  const headerIndex = rows.findIndex(r => r.includes('Name') && r.includes('Status') && r.includes('FY'));
  if (headerIndex < 0) throw new Error(`Could not find the header row in "${ROSTER_SHEET}".`);
  const header = rows[headerIndex];
  const columnIndex = {};
  for (const [key, label] of Object.entries(CANDIDATE_COLUMNS)) {
    const i = header.indexOf(label);
    if (i < 0) throw new Error(`Column "${label}" not found in "${ROSTER_SHEET}".`);
    columnIndex[key] = i;
  }

  return rows
    .slice(headerIndex + 1)
    .filter(r => cleanText(r[columnIndex.CandidateName]))
    .map(r => {
      const candidate = {};
      for (const [key, i] of Object.entries(columnIndex)) {
        if (DATE_COLUMNS.has(key)) candidate[key] = serialToDate(r[i]);
        else if (key === 'DaysOfProcess') candidate[key] = typeof r[i] === 'number' ? Math.round(r[i]) : null;
        else candidate[key] = cleanText(r[i]);
      }
      // The sheet computes the days from the people-sharing date; without one it yields TODAY() minus 0.
      if (!candidate.PeopleSharingDate) candidate.DaysOfProcess = null;
      return candidate;
    });
}

function parseHeadcount(workbook) {
  const rows = sheetRows(workbook, DASHBOARD_SHEET);
  const dateRow = rows[DATE_ROW] || [];
  const monthNameRow = rows[MONTH_NAME_ROW] || [];

  const months = [];
  dateRow.forEach((value, col) => {
    if (col <= LABEL_COL || !cleanText(monthNameRow[col])) return;
    const date = serialToDate(value);
    if (!date) return;
    const [y, m] = date.split('-').map(Number);
    months.push({ col, month: `${y}-${pad(m)}-01`, fy: fiscalYearOf(y, m) });
  });

  const result = [];
  const seen = new Set();
  let sortOrder = 0;
  for (let r = FIRST_METRIC_ROW; r < rows.length; r++) {
    let label = cleanText(rows[r]?.[LABEL_COL]);
    if (label === METRICS_END_LABEL) break;
    if (!label) continue;
    // The sheet labels the TO delta row "Delta (BC)" a second time (its formula is TO headcount - TO budget).
    if (label === 'Delta (BC)' && seen.has(label)) label = 'Delta (TO)';
    seen.add(label);
    // Breakdown rows like "Headcount (TC)" sit under their total; "(1st of Month)" / "(End of Month)" are totals.
    const level = /\((TC|BC|TO|Contractors)\)$/.test(label) ? 1 : 0;
    sortOrder += 1;
    for (const { col, month, fy } of months) {
      const value = rows[r][col];
      result.push({ FY: fy, Metric: label, SortOrder: sortOrder, Level: level, Month: month, Value: typeof value === 'number' ? value : null });
    }
  }
  return result;
}

export default function parseRecruitmentWorkbook(workbook) {
  return {
    candidates: parseCandidates(workbook),
    headcount: parseHeadcount(workbook)
  };
}
